/**
 * AllysListingsContent - Allys-themed Properties/Listings Page Content
 * 
 * Uses full Listings API data with Allys styling:
 * - Navy (#243462) and Gold (#BBA339) color scheme
 * - Agent avatars with name popovers
 * - Modern filtering and sorting
 * - Development grouping: properties belonging to a development are hidden
 *   and the development is shown as a single card instead
 */

'use client';

import { useState, useMemo, useEffect } from 'react';
import type { ListingOnChannel } from '@/lib/api/clients/listings';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import {
  ListingFilters,
  defaultFilters,
  transformListingToCardData,
} from '@/components/listings';
import type { ListingFiltersState, ListingCardData } from '@/components/listings';
import { AllysListingGrid } from './AllysListingGrid';
import { AllysProjectCard } from './AllysProjectCard';
import { AllysViewSwitcher, type ViewMode } from './AllysViewSwitcher';
import { AllysListingsMapView } from './AllysListingsMapView';

const SLUG_TO_CODE: Record<string, string> = {
  villa: 'VILLA',
  residential: 'RESIDENTIAL',
  apartment: 'APARTMENT',
  commercial: 'COMMERCIAL',
  land: 'LAND',
  house: 'HOUSE',
  penthouse: 'PENTHOUSE',
};

interface Props {
  listings: ListingOnChannel[];
  locale: string;
  messages: Record<string, unknown>;
  basePath: string;
  propertyTypes?: Array<{ id: string; name: string; code?: string }>;
  initialPropertyTypeSlug?: string | null;
  initialMinBedrooms?: number | null;
  initialMinBathrooms?: number | null;
  /** Development projects whose units appear in the listings -- shown as single cards */
  developmentProjects?: PropertyDevelopmentProject[];
}

export function AllysListingsContent({
  listings,
  locale,
  messages,
  basePath,
  propertyTypes = [],
  initialPropertyTypeSlug = null,
  initialMinBedrooms = null,
  initialMinBathrooms = null,
  developmentProjects = [],
}: Props) {
  // Transform API listings to card data
  const listingCardData = useMemo(() => {
    return listings
      .map(transformListingToCardData)
      .filter((l): l is ListingCardData => l !== null);
  }, [listings]);

  // Build a set of development IDs that have project cards
  const developmentIdSet = useMemo(() => {
    return new Set(developmentProjects.map((p) => p.id));
  }, [developmentProjects]);

  // Separate standalone listings from development-grouped listings
  const standaloneListings = useMemo(() => {
    if (developmentIdSet.size === 0) return listingCardData;
    return listingCardData.filter((l) => !l.development || !developmentIdSet.has(l.development.id));
  }, [listingCardData, developmentIdSet]);

  // Resolve the URL slug to a property type ID using multiple strategies:
  // 1. Match against propertyTypes from the getPropertyTypes() API (code-based)
  // 2. Fallback: match slug against propertyType.name in the actual listing data
  const resolvedTypeId = useMemo(() => {
    if (!initialPropertyTypeSlug) return null;
    const slugLower = initialPropertyTypeSlug.toLowerCase();

    const code = SLUG_TO_CODE[slugLower];
    if (code) {
      const apiMatch = propertyTypes.find(pt => pt.code?.toUpperCase() === code);
      if (apiMatch) return apiMatch.id;
    }

    const nameMatch = listingCardData.find(
      l => l.propertyType?.name?.toLowerCase() === slugLower
    );
    return nameMatch?.propertyType?.id ?? null;
  }, [initialPropertyTypeSlug, propertyTypes, listingCardData]);

  const [filters, setFilters] = useState<ListingFiltersState>(() => ({
    ...defaultFilters,
    propertyTypeId: null,
    minBedrooms: initialMinBedrooms ?? null,
    minBathrooms: initialMinBathrooms ?? null,
  }));

  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  useEffect(() => {
    if (resolvedTypeId && filters.propertyTypeId !== resolvedTypeId) {
      setFilters(prev => ({ ...prev, propertyTypeId: resolvedTypeId }));
    }
  }, [resolvedTypeId]);
  
  // Apply filters to standalone listings only (development units are hidden)
  const filteredListings = useMemo(() => {
    return standaloneListings
      .filter((listing) => {
        // Search filter — matches against name, headline, and location fields
        if (filters.search) {
          const search = filters.search.toLowerCase();
          const searchableFields = [
            listing.property.name,
            listing.mainHeadline,
            listing.location?.addressLine1,
            listing.location?.suburb,
            listing.location?.city,
            listing.location?.district,
            listing.location?.region,
            listing.location?.name,
          ];
          const matched = searchableFields.some(
            field => field && field.toLowerCase().includes(search)
          );
          if (!matched) return false;
        }
        
        // Listing type filter
        if (filters.listingType !== 'ALL' && listing.listingType !== filters.listingType) {
          return false;
        }
        
        // Status filter
        if (filters.status !== 'ALL' && listing.status !== filters.status) {
          return false;
        }
        
        // Price filter
        if (filters.minPrice !== null && (!listing.price || listing.price.amount < filters.minPrice)) {
          return false;
        }
        if (filters.maxPrice !== null && (!listing.price || listing.price.amount > filters.maxPrice)) {
          return false;
        }
        
        // Bedrooms filter
        if (filters.minBedrooms !== null && (listing.property.bedrooms === null || listing.property.bedrooms < filters.minBedrooms)) {
          return false;
        }
        
        // Bathrooms filter
        if (filters.minBathrooms !== null && (listing.property.bathrooms === null || listing.property.bathrooms < filters.minBathrooms)) {
          return false;
        }
        
        // Property type filter
        if (filters.propertyTypeId !== null && listing.propertyType?.id !== filters.propertyTypeId) {
          return false;
        }
        
        return true;
      })
      .sort((a, b) => {
        switch (filters.sortBy) {
          case 'newest':
            return (b.listedDate || '').localeCompare(a.listedDate || '');
          case 'price_asc':
            return (a.price?.amount || 0) - (b.price?.amount || 0);
          case 'price_desc':
            return (b.price?.amount || 0) - (a.price?.amount || 0);
          case 'bedrooms':
            return (b.property.bedrooms || 0) - (a.property.bedrooms || 0);
          default:
            return 0;
        }
      });
  }, [standaloneListings, filters]);

  // Filter development projects by search term if applicable
  const filteredProjects = useMemo(() => {
    if (developmentProjects.length === 0) return [];
    if (!filters.search) return developmentProjects;
    const search = filters.search.toLowerCase();
    return developmentProjects.filter((p) =>
      p.name.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search)
    );
  }, [developmentProjects, filters.search]);

  const safeGetString = (obj: unknown, defaultValue: string): string => {
    if (typeof obj === 'string') return obj;
    return defaultValue;
  };

  const plMsg = (messages?.propertiesList || messages?.properties || {}) as Record<string, unknown>;
  const statsMsg = plMsg?.stats as Record<string, unknown> | undefined;
  const resultsMsg = plMsg?.results as Record<string, unknown> | undefined;
  const emptyMsg = plMsg?.empty as Record<string, unknown> | undefined;
  const emptyMessage = safeGetString(emptyMsg?.title, 'No listings available');
  const emptyDescription = safeGetString(emptyMsg?.description, 'Check back later for new property listings.');

  return (
    <div className={`min-h-screen bg-gray-50 font-inter ${viewMode === 'map' ? 'h-screen flex flex-col overflow-hidden' : ''}`}>
      {/* Hero Section - Allys styled (collapses in map view) */}
      {viewMode !== 'map' && (
        <section className="bg-gradient-to-b from-[#243462]/5 to-gray-50 pt-20 sm:pt-24 pb-6 sm:pb-12 px-4 md:px-8">
          <div className="container mx-auto max-w-7xl">
            {/* Decorative accent */}
            <div className="w-24 h-1.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] rounded-full mb-4 sm:mb-8" />
            
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#243462] mb-3 sm:mb-4 tracking-tight">
              {safeGetString(plMsg?.title, 'Property Listings')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              {safeGetString(plMsg?.subtitle, 
                'Discover your perfect property from our curated collection of homes, apartments, and investment opportunities.')}
            </p>
            
            {/* Stats - Allys styled */}
            <div className="flex flex-wrap gap-4 sm:gap-8 mt-4 sm:mt-8">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-extrabold text-[#243462]">{listings.length}</span>
                <span className="text-gray-600 font-medium">{safeGetString(statsMsg?.totalListings, 'Total Listings')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-extrabold text-[#243462]">
                  {listings.filter(l => l.listing?.listingType === 'SALE').length}
                </span>
                <span className="text-gray-600 font-medium">{safeGetString(statsMsg?.forSale, 'For Sale')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-extrabold text-[#BBA339]">
                  {listings.filter(l => 
                    l.listing?.listingType === 'RENTAL' || 
                    l.listing?.listingType === 'HOLIDAY_RENTAL'
                  ).length}
                </span>
                <span className="text-gray-600 font-medium">{safeGetString(statsMsg?.forRent, 'For Rent')}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Nav spacer in map mode — the fixed nav is out of document flow */}
      {viewMode === 'map' && <div className="h-[88px] flex-shrink-0" />}
      
      {/* Filters + View Switcher */}
      <section className={
        viewMode === 'map'
          ? 'relative z-30 bg-white border-b border-gray-200 shadow-sm flex-shrink-0'
          : 'sm:sticky sm:top-16 z-30 bg-white border-b border-gray-200 shadow-sm'
      }>
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <ListingFilters
                filters={filters}
                onChange={setFilters}
                propertyTypes={propertyTypes}
                showAdvanced={true}
              />
            </div>
            <div className="flex-shrink-0">
              <AllysViewSwitcher
                activeView={viewMode}
                onViewChange={setViewMode}
                labels={{
                  grid: safeGetString(plMsg?.viewGrid, 'Grid'),
                  list: safeGetString(plMsg?.viewList, 'List'),
                  map: safeGetString(plMsg?.viewMap, 'Map'),
                }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Results */}
      <section className={viewMode === 'map' ? 'flex-1 min-h-0' : 'py-10 px-4 md:px-8'}>
        <div className={viewMode === 'map' ? 'h-full' : 'container mx-auto max-w-7xl'}>
          {/* Results count (non-map views) */}
          {viewMode !== 'map' && (
            <div className="flex items-center justify-between mb-8">
              <p className="text-gray-600">
                {safeGetString(resultsMsg?.showing, 'Showing')}{' '}
                <span className="font-bold text-[#243462]">
                  {filteredListings.length + filteredProjects.length}
                </span> 
                {(filteredListings.length + filteredProjects.length) === 1 
                  ? ` ${safeGetString(resultsMsg?.listing, 'listing')}` 
                  : ` ${safeGetString(resultsMsg?.listings, 'listings')}`}
                {filters.listingType !== 'ALL' && (
                  <span className="text-[#BBA339] font-semibold"> &bull; {filters.listingType}</span>
                )}
              </p>
            </div>
          )}

          {/* Map view */}
          {viewMode === 'map' ? (
            <AllysListingsMapView
              listings={filteredListings}
              basePath={basePath}
              locale={locale}
              messages={plMsg as Record<string, unknown>}
              height="100%"
            />
          ) : (
            <div>
              {/* Development Project Cards */}
              {filteredProjects.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {filteredProjects.map((project) => (
                    <AllysProjectCard
                      key={project.id}
                      project={project}
                      href={`${basePath}/projects/${project.slug || project.id}`}
                      messages={{
                        availableUnits: safeGetString(plMsg?.availableUnits, 'Available Units'),
                        soldOut: safeGetString(plMsg?.soldOut, 'Sold Out'),
                        comingSoon: safeGetString(plMsg?.comingSoon, 'Coming Soon'),
                        enquireNow: safeGetString(plMsg?.enquireNow, 'Enquire Now'),
                        viewProject: safeGetString(plMsg?.viewProject, 'View Development'),
                        startingFrom: safeGetString(plMsg?.startingFrom, 'Starting from'),
                        explore: safeGetString(plMsg?.explore, 'Explore'),
                      }}
                      locale={locale}
                    />
                  ))}
                </div>
              )}
              
              {/* Listings Grid/List - Allys themed (standalone listings only) */}
              <AllysListingGrid
                listings={filteredListings}
                basePath={basePath}
                variant={viewMode === 'list' ? 'list' : 'grid'}
                columns={3}
                showAgent={false}
                showAgentAvatar={true}
                showStatus={true}
                locale={locale}
                messages={plMsg as Record<string, unknown>}
                emptyMessage={filteredProjects.length > 0 ? '' : emptyMessage}
                emptyDescription={filteredProjects.length > 0 ? '' : emptyDescription}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default AllysListingsContent;
